import { PaymentFlow, PaymentHandling } from '@primer-io/checkout-web'

import { Cohort, ProductKey } from 'root-constants/common'

export const EMPTY_FIELD_ERROR = `can't be blank`

export const DEFAULT_CARDHOLDER_NAME = ' '

export const enum StripeErrorCode {
  CARD_DECLINED = 'card_declined',
}

export const enum PaymentMethod {
  CREDIT_CARD = 'credit_card',
  APPLE_PAY = 'apple_pay',
  GOOGLE_PAY = 'google_pay',
  PAYPAL = 'paypal',
  LINK = 'link',
  BOLETO = 'boleto',
  PIX = 'pix',
  UPI = 'upi',
}

export const enum PaymentSystem {
  STRIPE = 'stripe',
  PAYPAL = 'paypal',
  PRIMER = 'primer',
}

export const THREEDS_REDIRECT_SEARCH_PARAM = 'threeds'

export const enum StripeSoftDeclineReason {
  AUTHENTICATION_FAILURE = 'payment_intent_authentication_failure',
  DO_NOT_HONOR = 'do_not_honor',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  INVALID_AMOUNT = 'invalid_amount',
  PROCESSING_ERROR = 'processing_error',
  REENTER_TRANSACTION = 'reenter_transaction',
  SERVICE_NOT_ALLOWED = 'service_not_allowed',
  TRANSACTION_NOT_ALLOWED = 'transaction_not_allowed',
  CARD_VELOCITY_EXCEEDED = 'card_velocity_exceeded',
  GENERIC_DECLINE = 'generic_decline',
  TRY_AGAIN_LATER = 'try_again_later',
}

export const enum StripeDeclineReason {
  STOLEN_CARD = 'stolen_card',
  LOST_CARD = 'lost_card',
  STRIPE_GENERIC_DECLINE_ERROR = 'generic_decline',
}

export const STRIPE_SOFT_DECLINE_REASONS = [
  StripeSoftDeclineReason.AUTHENTICATION_FAILURE,
  StripeSoftDeclineReason.DO_NOT_HONOR,
  StripeSoftDeclineReason.INSUFFICIENT_FUNDS,
  StripeSoftDeclineReason.INVALID_AMOUNT,
  StripeSoftDeclineReason.PROCESSING_ERROR,
  StripeSoftDeclineReason.REENTER_TRANSACTION,
  StripeSoftDeclineReason.SERVICE_NOT_ALLOWED,
  StripeSoftDeclineReason.TRANSACTION_NOT_ALLOWED,
  StripeSoftDeclineReason.CARD_VELOCITY_EXCEEDED,
  StripeSoftDeclineReason.GENERIC_DECLINE,
  StripeSoftDeclineReason.TRY_AGAIN_LATER,
]

export const STRIPE_ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        'https://fonts.googleapis.com/css2?family=Nunito:wght@400&display=block',
    },
  ],
}

export const PERSONAL_DISCOUNT_AMOUNT = 35

export const INITIAL_PRIMER_CONFIG = {
  card: {
    cardholderName: {
      required: false,
    },
  },
  style: {
    stylesheets: [
      {
        href: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap',
      },
    ],
  },
  paymentHandling: 'MANUAL' as PaymentHandling,
  paypal: {
    paymentFlow: PaymentFlow.PREFER_VAULT,
  },
}

export const PRIMER_PAYMENT_ERRORS = {
  INSUFFICIENT_FUNDS: 'payment.insufficientFunds',
  CONNECTION_ERROR: 'payment.connectionError',
  PAYMENT_FAILED: 'payment.insufficientFunds',
  DECLINED: 'payment.declinedError',
  AUTHENTICATION_REQUIRED: 'payment.authenticationRequired',
  WITHDRAWAL_LIMIT_EXCEEDED: 'payment.withdrawLimit',
  EXPIRED_CARD: 'payment.expiredCard',
  COMMON_ERROR: 'commonComponents.commonError',
  ERROR: 'commonComponents.commonError',
  INVALID_CARD_NUMBER: 'commonComponents.commonError',
  LOST_OR_STOLEN_CARD: 'commonComponents.commonError',
  SUSPECTED_FRAUD: 'commonComponents.commonError',
  UNKNOWN: 'commonComponents.commonError',
  REFER_TO_CARD_ISSUER: 'commonComponents.commonError',
  DO_NOT_HONOR: 'commonComponents.commonError',
  ISSUER_TEMPORARILY_UNAVAILABLE: 'commonComponents.commonError',
}

export const PRIMER_PAY_PAL_ID = 'paypal-button'
export const PRIMER_CARD_NUMBER_ID = 'checkout-card-number-input'
export const PRIMER_CARD_EXPIRY_ID = 'checkout-card-expiry-input'
export const PRIMER_CARD_CVC_ID = 'checkout-card-cvv-input'
export const PRIMER_CARD_HOLDER_ID = 'checkout-card-holder-input'
export const CARDHOLDER_NAME_REGEX =
  /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/
export const UPI_ID_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z]{3,}$/
export const UPI_FIELD_MAX_LENGTH = 255
export const CPF_REGEX = /^(\d{3})(\d{3})(\d{3})(\d{2})$/
export const POSTAL_CODE_REGEX = /^(\d{5})(\d{3})$/
export const INDIA_POSTAL_CODE_REGEX = /^\d{6}$/
export const INDIA_POSTAL_CODE_LENGTH = 6

export const CARD_PAYMENT_FIELD_NAMES = {
  NAME: 'name',
  NUMBER: 'cardNumber',
  EXPIRY: 'cardExpiry',
  CVC: 'cardCvc',
} as const

export const PRIMER_ERROR_CODE = {
  CARD_REQUIRED: 'cardRequired',
  CARD_IS_INCOMPLETE: 'cardIncomplete',
  CARD_IS_INVALID: 'cardInvalid',
  EXPIRY_REQUIRED: 'expiryRequired',
  CARD_IS_EXPIRED: 'cardExpired',
  CARD_EXPIRY_YEAR_INVALID: 'expiryYearInvalid',
  CARD_EXPIRY_MONTH_INVALID: 'expiryMonthInvalid',
  CVV_IS_INCOMPLETE: 'cvvIncomplete',
  CVV_IS_INVALID3: 'cvvInvalid3',
  CVV_IS_INVALID4: 'cvvInvalid4',
}

export const CARD_CVC_PLACEHOLDER = '111'
export const CARD_NUMBER_PLACEHOLDER = '1234 1234 1234 1234'
export const FIVE_MINUTES_IN_SECONDS = 300
export const TIMER_START_MINUTES = '05'
export const TIMER_START_SECONDS = '00'
export const DYNAMIC_DISCOUNT_CONTAINER_WITH_TIMER_THEME = {
  red: 'linear-gradient(87deg, #F7DBDB -4.33%, #FEE4E5 111.17%)',
  blue: 'linear-gradient(87deg, #E2D4FF -3.7%, #D8EDFF 119.47%)',
  orange: 'linear-gradient(88deg, #F7D2E0 -0.53%, #F7EBDE 108.08%)',
}

export const PRICE_DISCLAIMER_FOR_USA = {
  [ProductKey.ONE_WEEK]: 'payment.descriptionWithoutExpiredTimerOneWeek',
  [ProductKey.ONE_MONTH]: 'payment.descriptionWithoutExpiredTimerOneMonth',
  [ProductKey.THREE_MONTHS]:
    'payment.descriptionWithoutExpiredTimerThreeMonths',
}

export const PRICE_DISCLAIMER_FOR_USA_FULL_PRICE = {
  [ProductKey.ONE_WEEK]: 'payment.descriptionWithExpiredTimerOneWeek',
  [ProductKey.ONE_MONTH]: 'payment.descriptionWithExpiredTimerOneMonth',
  [ProductKey.THREE_MONTHS]: 'payment.descriptionWithExpiredTimerThreeMonths',
}

export const COHORTS_WITH_DISCLAIMER_FOR_PAYMENT5: string[] = [
  Cohort.MAIN_SAFETY,
  Cohort.MAIN_SAFETY_MAP,
  Cohort.MAIN_SAFETY_V3,
  Cohort.MAIN_SAFETY_V2,
  Cohort.MAIN_SAFETY2_APPL,
]
